import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faDiamondExclamation,
  faInfoCircle,
} from "@fortawesome/sharp-solid-svg-icons"
import { Box, Typography } from "@mui/material"
import type { WastewaterPCRActivityLevel } from "@phc/common/gen/threatAnalyticsGen"
import { useMemo } from "react"
import { generatePath, Link } from "react-router"
import { FAQ_POST_SLUGS, ROUTES } from "../../utils/constants"
import { extraColors } from "../../utils/theme"
import { CurrentActivityChip } from "../Charts/LegendStyles"
import type { BaseLegendItemData, ShapeType } from "../Charts/legendUtils"
import { TimeLegend, type ColumnDefinition } from "../Charts/TimeLegend"
import { LightTooltip } from "../LocationDetails/LocationRisk/Shared"
import {
  getLevel,
  getTrendIcon,
  hasActivity,
  makeAnomalyKey,
} from "./WastewaterUtils"

export interface LegendItemData extends BaseLegendItemData {
  shape: ShapeType
  latestAnalysis?: WastewaterPCRActivityLevel
}

interface WastewaterLegendProps {
  items: LegendItemData[]
  hiddenIds: Set<string>
  onToggle: (id: string) => void
  onToggleAll: () => void
  anomalyKeys?: Set<string>
}

export const WastewaterLegend = ({
  items,
  hiddenIds,
  onToggle,
  onToggleAll,
  anomalyKeys,
}: WastewaterLegendProps) => {
  const columns = useMemo<ColumnDefinition<LegendItemData>[]>(() => {
    return [
      {
        header: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <Link
              to={generatePath(ROUTES.FAQ_POST, {
                slug: FAQ_POST_SLUGS.WASTEWATER_METHODLOGY,
              })}
            >
              <LightTooltip
                title="Wastewater Methodology"
                arrow
                placement="top-end"
              >
                <Box sx={{ cursor: "pointer" }}>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{
                      fontSize: 16,
                      color: extraColors.status.navyLight,
                      paddingRight: 4,
                    }}
                  />
                </Box>
              </LightTooltip>
            </Link>

            <Typography variant="small1">CURRENT ACTIVITY</Typography>
          </Box>
        ),
        render: item => {
          const level = getLevel(item.latestAnalysis)
          const trendIcon = getTrendIcon(item.latestAnalysis)
          const hasAnomaly =
            item.latestAnalysis != null &&
            (anomalyKeys?.has(
              makeAnomalyKey(
                item.latestAnalysis.target_name,
                item.latestAnalysis.sampling_end_date
              )
            ) ??
              false)

          return (
            <CurrentActivityChip level={level}>
              <FontAwesomeIcon
                icon={faDiamondExclamation}
                style={{ fontSize: 10, opacity: hasAnomaly ? 1 : 0 }}
                aria-hidden={!hasAnomaly}
                role={hasAnomaly ? "img" : undefined}
                aria-label={hasAnomaly ? "Anomaly detected" : undefined}
              />
              <span>{level}</span>
              {hasActivity(level) ? (
                <FontAwesomeIcon icon={trendIcon} style={{ fontSize: 12 }} />
              ) : (
                <Box
                  sx={{
                    width: 12,
                  }}
                />
              )}
            </CurrentActivityChip>
          )
        },
      },
    ]
  }, [anomalyKeys])

  return (
    <TimeLegend
      items={items}
      hiddenIds={hiddenIds}
      onToggle={onToggle}
      onToggleAll={onToggleAll}
      columns={columns}
      searchable={true}
      searchPlaceholder="Search"
      helperText="Select a row to isolate it in the chart"
      headerTitle="PATHOGEN"
    />
  )
}
